import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import type { StatusPillVariant } from '@/shared/components/ui';
import type { PopulatedLead } from '../types';

/**
 * Helpers de formatação/derivação compartilhados entre a linha de tabela
 * (LeadListRow) e o card (LeadGridCard) da listagem de leads.
 *
 * Específico desta tela — não é barrel compartilhado.
 */

export type LeadSortKey = 'date' | 'name' | 'value' | 'priority' | 'status' | 'qualify' | 'funnel';

export const formatLeadCurrency = (value?: number): string => {
  if (value == null) return '—';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

export const formatLeadShortDate = (dateString?: string): string => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear()).slice(-2);
  return `${day}/${month}/${year}`;
};

export const formatLeadRelative = (dateString?: string): string => {
  if (!dateString) return '';
  try {
    return formatDistanceToNow(new Date(dateString), { addSuffix: true, locale: ptBR });
  } catch {
    return '';
  }
};

export const getLeadInitials = (name?: string): string => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.charAt(0) ?? '';
  const second = parts.length > 1 ? parts[parts.length - 1]?.charAt(0) ?? '' : '';
  return `${first}${second}`.toUpperCase() || '?';
};

export const getLeadChannelLabel = (channel?: string): string => {
  switch (channel) {
    case 'whatsapp': return 'WhatsApp';
    case 'instagram': return 'Instagram';
    case 'facebook': return 'Facebook';
    case 'messenger': return 'Messenger';
    case 'telegram': return 'Telegram';
    case 'email': return 'E-mail';
    case 'website': return 'Website';
    case 'phone': return 'Telefone';
    case 'google': return 'Google';
    case 'youtube': return 'YouTube';
    case 'tiktok': return 'TikTok';
    case 'linkedin': return 'LinkedIn';
    case 'bing': return 'Bing';
    case 'referral': return 'Indicação';
    case 'other': return 'Outro';
    default: return '—';
  }
};

export interface LeadPriorityMeta { color: string; label: string; rank: number }

// Cores do PRIO do mockup crm-leads.html (pbadge: tint `${color}1a` + dot currentColor)
export const getLeadPriorityMeta = (priority?: string): LeadPriorityMeta => {
  switch (priority) {
    case 'urgent': return { color: '#ef4444', label: 'Urgente', rank: 4 };
    case 'high': return { color: '#d97706', label: 'Alta', rank: 3 };
    case 'medium': return { color: '#3b82f6', label: 'Média', rank: 2 };
    case 'low': return { color: '#94a3b8', label: 'Baixa', rank: 1 };
    default: return { color: '#94a3b8', label: 'Não definida', rank: 0 };
  }
};

export type BadgeVariant = 'success' | 'warning' | 'destructive' | 'info';

export const getActivityBadge = (activityStatus?: string): { variant: BadgeVariant; label: string } => {
  switch (activityStatus) {
    case 'up_to_date': return { variant: 'success', label: 'Em dia' };
    case 'overdue': return { variant: 'warning', label: 'Atrasado' };
    case 'no_activities': return { variant: 'destructive', label: 'Sem atividades' };
    default: return { variant: 'info', label: 'Não definido' };
  }
};

// Cores exatas dos pills do mockup crm-leads.html (spill/qpill: tint `${color}1a` + dot)
export const getBusinessBadge = (businessStatus?: string): { variant: BadgeVariant; label: string; rank: number; color: string } => {
  switch (businessStatus) {
    case 'won': return { variant: 'success', label: 'Ganho', rank: 2, color: '#0f9d6b' };
    case 'lost': return { variant: 'destructive', label: 'Perdido', rank: 0, color: '#ef4444' };
    default: return { variant: 'warning', label: 'Pendente', rank: 1, color: '#3b82f6' };
  }
};

/** Variant do StatusPill shared (F62) para o status de negócio: ganho=verde, perdido=vermelho, pendente=azul (em andamento). */
export const getBusinessPillVariant = (businessStatus?: string): StatusPillVariant => {
  switch (businessStatus) {
    case 'won': return 'success';
    case 'lost': return 'error';
    default: return 'info';
  }
};

/** Variant do StatusPill shared (F62) para a qualificação: qualificado=verde, desqualificado=vermelho, pendente=neutro. */
export const getQualifyPillVariant = (qualifyStatus?: string): StatusPillVariant => {
  switch (qualifyStatus) {
    case 'qualified': return 'success';
    case 'disqualified': return 'error';
    default: return 'neutral';
  }
};

export const getQualifyBadge = (qualifyStatus?: string): { variant: BadgeVariant; label: string; rank: number; color: string } => {
  switch (qualifyStatus) {
    case 'qualified': return { variant: 'success', label: 'Qualificado', rank: 2, color: '#0f9d6b' };
    case 'disqualified': return { variant: 'destructive', label: 'Desqualificado', rank: 0, color: '#ef4444' };
    default: return { variant: 'info', label: 'Pendente', rank: 1, color: '#94a3b8' };
  }
};

/** Alerta de inatividade ativo (cor computada server-side ou atraso de atividade). */
export const hasLeadAlert = (lead: Pick<PopulatedLead, 'alertLevel' | 'activityStatus'>): boolean => {
  if (lead.alertLevel === 'critical' || lead.alertLevel === 'warning') return true;
  if (!lead.alertLevel && lead.activityStatus === 'overdue') return true;
  return false;
};

export const getLeadEmail = (lead: PopulatedLead): string =>
  lead.contact?.identifiers?.email?.[0] || lead.contact?.email || 'Sem email';

/** Comparador client-side para ordenação da página carregada. */
export const compareLeads = (a: PopulatedLead, b: PopulatedLead, key: LeadSortKey): number => {
  switch (key) {
    case 'name':
      return (a.contact?.name || '').localeCompare(b.contact?.name || '', 'pt-BR');
    case 'funnel':
      return (a.funnel?.name || '').localeCompare(b.funnel?.name || '', 'pt-BR');
    case 'value':
      return (a.budget ?? 0) - (b.budget ?? 0);
    case 'priority':
      return getLeadPriorityMeta(a.priority).rank - getLeadPriorityMeta(b.priority).rank;
    case 'status':
      return getBusinessBadge(a.businessStatus).rank - getBusinessBadge(b.businessStatus).rank;
    case 'qualify':
      return getQualifyBadge(a.qualifyStatus).rank - getQualifyBadge(b.qualifyStatus).rank;
    case 'date':
    default:
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
  }
};
